import { Button } from "@/components/ui/button";
import { useNavigate, Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Check } from "lucide-react";
import { CreditPackages } from "@/components/subscription/CreditPackages";

const PricingTier = ({
  name,
  price,
  description,
  features,
  popular = false,
}: {
  name: string;
  price: string;
  description: string;
  features: string[];
  popular?: boolean;
}) => (
  <Card className={`w-full ${popular ? 'border-primary shadow-lg' : ''}`}>
    <CardHeader>
      <CardTitle className="text-2xl">{name}</CardTitle>
      <CardDescription>{description}</CardDescription>
    </CardHeader>
    <CardContent>
      <div className="mb-4">
        <span className="text-4xl font-bold">{price}</span>
        <span className="text-gray-500 ml-2">/month</span>
      </div>
      <ul className="space-y-3">
        {features.map((feature, index) => (
          <li key={index} className="flex items-center gap-2">
            <Check className="h-5 w-5 text-green-500" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
    </CardContent>
    <CardFooter>
      <Button className="w-full" variant={popular ? "default" : "outline"}>
        Get Started
      </Button>
    </CardFooter>
  </Card>
);

const Pricing = () => {
  const navigate = useNavigate();

  const tiers = [
    {
      name: "Intro",
      price: "$99",
      description: "For personal use",
      features: [
        "All features",
        "Export data in JSON & CSV",
        "1,000 credits per month",
        "1 seat",
        "API access"
      ]
    },
    {
      name: "Explorer",
      price: "$199",
      description: "For professionals",
      features: [
        "All Intro features",
        "Mail support",
        "3,000 credits per month",
        "Access to beta features",
        "3 seats"
      ],
      popular: true
    },
    {
      name: "Enterprise",
      price: "$499",
      description: "For large teams",
      features: [
        "All Explorer features",
        "10K credits per month",
        "Dedicated account manager",
        "Custom feature requests",        
        "5 seats",
      ]
    }
  ];

  const handleBuyCredits = (amount: number) => {
    navigate('/subscription');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <Link to="/">
          <Button variant="ghost" className="mb-8">
            ← Back to Home
          </Button>
        </Link>

        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Simple, transparent pricing
          </h1>
          <p className="text-xl text-gray-600">
            Choose the plan that best fits your needs
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {tiers.map((tier) => (
            <PricingTier key={tier.name} {...tier} />
          ))}
        </div>

        <div className="mb-12">
          <CreditPackages onBuyCredits={handleBuyCredits} />
        </div>

        <div className="max-w-3xl mx-auto text-center bg-white p-8 rounded-lg shadow-sm">
          <h2 className="text-2xl font-semibold mb-4">Need a custom solution?</h2>
          <p className="text-gray-600 mb-6">
            Contact us for custom enterprise solutions, volume discounts, and special requirements.
          </p>
          <Button
            variant="outline"
            onClick={() => navigate('/contact')}
          >
            Contact Sales
          </Button>
        </div>

        <div className="mt-12 text-center text-gray-600">
          <p className="mb-4">All plans include:</p>
          <ul className="text-sm space-y-2">
            <li>Automatic monthly credit renewal</li>
            <li>Flexible payment options</li>
            <li>Cancel anytime</li>
            <li>SSL security</li>
            <li>Regular feature updates</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Pricing;